import React, { useEffect, useState, useCallback } from 'react';
import { Card, Collapse, Tabs, Tag, Empty, Button, Modal, Typography, Row, Col, Space, Switch, Tooltip, message as antMessage, Spin } from 'antd';
import { DollarOutlined, HistoryOutlined, WalletOutlined, UserDeleteOutlined, AlertOutlined, PauseCircleOutlined, PlayCircleOutlined, ExclamationCircleOutlined, PlusOutlined, EditOutlined, FileTextOutlined, WarningOutlined } from '@ant-design/icons';
import { fetchSettingsSummary } from '../../services/chargesApi';
import api from '../../services/api';
import SummaryCards from './charges/SummaryCards';
import HistoryTab from './charges/HistoryTab';
import PaymentsTab from './charges/PaymentsTab';
import FreePlayersTab from './charges/FreePlayersTab';
import DebtorsTab from './charges/DebtorsTab';
import DefaultersTab from './charges/DefaultersTab';
import ChargeSettingFormModal from './charges/ChargeSettingFormModal';
import MercadoPagoConnectionCard from './charges/MercadoPagoConnectionCard';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const statusTag = (status) => {
  const map = {
    ACTIVE:     { color: 'green',  label: 'Ativa' },
    PROCESSED:  { color: 'green',  label: 'Ativa' },
    PAUSED:     { color: 'orange', label: 'Pausada' },
    INACTIVE:   { color: 'default', label: 'Inativa' },
    CANCELED:   { color: 'red',    label: 'Cancelada' },
  };
  const m = map[status] || { color: 'default', label: status || '-' };
  return <Tag color={m.color}>{m.label}</Tag>;
};

const Charges = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [providerStatus, setProviderStatus] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const loadProviderStatus = useCallback(async () => {
    try {
      const response = await api.get('/organizer-charges/provider-status');
      setProviderStatus(response.data);
    } catch (error) {
      console.error('Erro ao verificar status do provedor:', error);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSettingsSummary({ include_inactive: showInactive ? '1' : '0' });
      setSettings(data.settings || []);
    } catch {
      antMessage.error('Erro ao carregar cobranças');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProviderStatus(); }, [loadProviderStatus]);

  const handleInactivate = (s, e) => {
    if (e) e.stopPropagation();
    Modal.confirm({
      title: 'Inativar cobrança',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p><strong>Descrição:</strong> {s.charge_description || '-'}</p>
          <p>Inativar bloqueia novas gerações. Faturas já emitidas continuam intactas (cancele manualmente se necessário).</p>
          <p>Deseja continuar?</p>
        </div>
      ),
      okText: 'Sim, inativar',
      okType: 'danger',
      cancelText: 'Voltar',
      onOk: async () => {
        try {
          await api.put(`/organizer-charges/settings/${s.id}/inactivate`);
          antMessage.success('Cobrança inativada');
          load();
        } catch (error) {
          const backendMessage = error?.response?.data?.message;
          antMessage.error(backendMessage || 'Erro ao inativar cobrança');
        }
      },
    });
  };

  const handleActivate = (s, e) => {
    if (e) e.stopPropagation();
    Modal.confirm({
      title: 'Ativar cobrança',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p><strong>Descrição:</strong> {s.charge_description || '-'}</p>
          <p>Ao ativar, a cobrança volta a ser gerada conforme a configuração.</p>
          <p>Deseja continuar?</p>
        </div>
      ),
      okText: 'Sim, ativar',
      cancelText: 'Voltar',
      onOk: async () => {
        try {
          await api.put(`/organizer-charges/settings/${s.id}/activation`, { action: 'ACTIVATE' });
          antMessage.success('Cobrança ativada');
          load();
        } catch (error) {
          const backendMessage = error?.response?.data?.message;
          antMessage.error(backendMessage || 'Erro ao ativar cobrança');
        }
      },
    });
  };

  const PanelHeader = ({ s }) => {
    const canInactivate = ['ACTIVE', 'PROCESSED'].includes(s.charge_status);
    const canActivate = s.charge_status === 'INACTIVE';
    return (
      <Row justify="space-between" align="middle" gutter={[8, 8]} wrap>
        <Col flex="auto">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <DollarOutlined />
            <strong style={{ fontSize: 16 }}>{s.charge_description}</strong>
            {statusTag(s.charge_status)}
          </div>
          <div style={{ color: '#8c8c8c', fontSize: 13, marginTop: 4 }}>
            <Text type="secondary">{s.charge_model_name}</Text>
            {s.charge_amount_recurring != null && (
              <> · Mensalista <strong>R$ {parseFloat(s.charge_amount_recurring).toFixed(2)}</strong></>
            )}
            {s.charge_amount_one_time != null && parseFloat(s.charge_amount_one_time) > 0 && (
              <> · Avulso <strong>R$ {parseFloat(s.charge_amount_one_time).toFixed(2)}</strong></>
            )}
          </div>
        </Col>
        <Col>
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); setEditingSetting(s); setFormOpen(true); }}
            >
              Editar
            </Button>
            {canActivate && (
              <Button
                size="small"
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={(e) => handleActivate(s, e)}
              >
                Ativar
              </Button>
            )}
            {canInactivate && (
              <Button
                size="small"
                icon={<PauseCircleOutlined />}
                onClick={(e) => handleInactivate(s, e)}
                style={{ color: '#faad14', borderColor: '#faad14' }}
              >
                Inativar
              </Button>
            )}
          </Space>
        </Col>
      </Row>
    );
  };

  if (loading && settings.length === 0) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  const chargeActive = providerStatus?.charge_active === true;

  const chargesTabContent = (
    <>
      <MercadoPagoConnectionCard
        providerStatus={providerStatus}
        onStatusChange={() => { loadProviderStatus(); load(); }}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#8c8c8c' }}>
          Mostrar canceladas/inativas
          <Switch
            size="small"
            checked={showInactive}
            onChange={setShowInactive}
          />
        </span>
        <Tooltip title={!chargeActive ? 'Vincule sua conta do Mercado Pago para criar cobranças' : ''}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!chargeActive}
            onClick={() => { setEditingSetting(null); setFormOpen(true); }}
          >
            Nova Cobrança
          </Button>
        </Tooltip>
      </div>

      {settings.length === 0 ? (
        <Card>
          <Empty
            description={
              <div>
                <p>Você ainda não tem cobranças configuradas.</p>
                <Tooltip title={!chargeActive ? 'Vincule sua conta do Mercado Pago para criar cobranças' : ''}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={!chargeActive}
                    onClick={() => { setEditingSetting(null); setFormOpen(true); }}
                  >
                    Criar primeira cobrança
                  </Button>
                </Tooltip>
              </div>
            }
          />
        </Card>
      ) : (
        <Collapse
          accordion
          activeKey={activeKey}
          onChange={(k) => setActiveKey(Array.isArray(k) ? k[0] : k)}
        >
          {settings.map((s) => (
            <Panel key={s.id} header={<PanelHeader s={s} />}>
              <SummaryCards setting={s} />
              <Tabs
                defaultActiveKey="history"
                items={[
                  {
                    key: 'history',
                    label: <span><HistoryOutlined /> Histórico</span>,
                    children: <HistoryTab settingId={s.id} refreshToken={refreshToken} />,
                  },
                  {
                    key: 'payments',
                    label: <span><WalletOutlined /> Pagamentos</span>,
                    children: <PaymentsTab settingId={s.id} />,
                  },
                  {
                    key: 'free',
                    label: <span><UserDeleteOutlined /> Isentos</span>,
                    children: <FreePlayersTab settingId={s.id} />,
                  },
                  {
                    key: 'debtors',
                    label: (
                      <span>
                        <AlertOutlined /> Devedores 30d+
                        {s.debtors_30d_count > 0 && (
                          <Tag color="red" style={{ marginLeft: 6 }}>{s.debtors_30d_count}</Tag>
                        )}
                      </span>
                    ),
                    children: <DebtorsTab settingId={s.id} />,
                  },
                ]}
              />
            </Panel>
          ))}
        </Collapse>
      )}
    </>
  );

  return (
    <div>
      <Title level={3} style={{ margin: 0, marginBottom: 12 }}>Cobranças</Title>
      <Tabs
        defaultActiveKey="charges"
        items={[
          {
            key: 'charges',
            label: <span><FileTextOutlined /> Cobranças</span>,
            children: chargesTabContent,
          },
          {
            key: 'defaulters',
            label: <span><WarningOutlined /> Inadimplentes</span>,
            children: <DefaultersTab />,
          },
        ]}
      />

      <ChargeSettingFormModal
        open={formOpen}
        initialValues={editingSetting}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); load(); setRefreshToken((t) => t + 1); }}
      />
    </div>
  );
};

export default Charges;
